import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { dashboardAPI } from '../utils/api';
import { FileText, Globe, UserX, Activity } from 'lucide-react';
import { format } from 'date-fns';

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_templates: 0,
    total_domains: 0,
    total_subscribers: 0,
    total_campaigns: 0,
    total_unsubscribes: 0,
    recent_campaigns: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      label: 'Total Templates',
      value: stats.total_templates,
      icon: FileText,
      color: '#183871'
    },
    {
      label: 'Total Subscribers',
      value: stats.total_subscribers,
      icon: UserX,
      color: '#06B6D4'
    },
    {
      label: 'Total Campaigns',
      value: stats.total_campaigns,
      icon: Activity,
      color: '#10B981'
    },
    {
      label: 'Active Domains',
      value: stats.total_domains,
      icon: Globe,
      color: '#F59E0B'
    }
  ];

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-[#F3F4F6] mb-2">
            Dashboard
          </h1>
          <p className="text-lg text-[#9CA3AF]">
            Overview of your email marketing platform
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}
                className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 backdrop-blur-sm p-6 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.5)] hover:border-[#183871]/50 transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <div 
                    className="p-3 rounded-md" 
                    style={{ backgroundColor: `${stat.color}20` }}
                  >
                    <Icon size={24} style={{ color: stat.color }} strokeWidth={1.5} />
                  </div>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#F3F4F6]">
                    {loading ? '-' : stat.value}
                  </p>
                  <p className="text-sm text-[#9CA3AF] mt-1">{stat.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Campaigns */}
        <div className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 backdrop-blur-sm p-6 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.5)]">
          <div className="flex items-center gap-3 mb-6">
            <Activity size={24} className="text-[#183871]" strokeWidth={1.5} />
            <h2 className="text-2xl font-semibold tracking-tight text-[#F3F4F6]">
              Recent Campaigns
            </h2>
          </div>

          {loading ? (
            <p className="text-[#9CA3AF] text-center py-8">Loading campaigns...</p>
          ) : !stats.recent_campaigns || stats.recent_campaigns.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#9CA3AF]">No campaigns yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {stats.recent_campaigns.map((campaign, index) => (
                <div
                  key={index}
                  data-testid={`campaign-${index}`}
                  className="flex items-center justify-between p-4 rounded-md bg-[#1F2128] border border-[#2A2D36] hover:border-[#183871]/50 transition-colors"
                >
                  <div>
                    <p className="text-[#F3F4F6] font-medium">{campaign.name}</p>
                    <p className="text-sm text-[#9CA3AF]">
                      {campaign.subject}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                      campaign.status === 'sent' ? 'bg-[#10B981]/20 text-[#10B981]' :
                      campaign.status === 'sending' ? 'bg-[#06B6D4]/20 text-[#06B6D4]' :
                      campaign.status === 'scheduled' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                      'bg-[#9CA3AF]/20 text-[#9CA3AF]'
                    }`}>
                      {campaign.status}
                    </span>
                    {campaign.created_at && (
                      <p className="text-xs text-[#9CA3AF] font-mono mt-1">
                        {format(new Date(campaign.created_at), 'MMM dd, yyyy')}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
